import Timeline from "@mui/lab/Timeline";
import TimelineDot from "@mui/lab/TimelineDot";
import InfoIcon from "@mui/icons-material/Info";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import { useEffect, useMemo, useRef, useState } from "react";
import TimelineOppositeContent, { timelineOppositeContentClasses } from "@mui/lab/TimelineOppositeContent";

import AlarmInfoPopup from "./AlarmInfoPopup";

import "./alarm-timeline-view.scss";
import { useLazyGetAlarmTimelineQuery } from "./alarmsSlice";
import { useSearchParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";

function titleCase(str: string) {
  var splitStr = str.split("_");
  for (var i = 0; i < splitStr.length; i++) {
    // You do not need to check if i is larger than splitStr length, as your for does that for you
    // Assign it back to the array
    splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
  }
  // Directly return the joined string
  return splitStr.join(" ");
}

type Alarm = {
  id: string;
  created_dt?: null | string;
  ack_dt?: null | string;
  updated_dt?: null | string;
  clear_dt?: null | string;
} & Record<string, any>;

const time_key_to_info_map = {
  created_dt: {
    title: "Alarm Created",
    sub_key_list: ["category", "create_info", "create_value", "severity"],
    type: "create",
  },
  ack_dt: { title: "Alarm Acked", sub_key_list: ["acked_info", "acked_by_user", "category"], type: "ack" },
  updated_dt: { title: "Alarm Updated", sub_key_list: ["update_value"], type: "update" },
  clear_dt: { title: "Alarm Cleared", sub_key_list: ["clear_info", "clear_value"], type: "clear" },
};

const required_time_key_list = ["created_dt", "ack_dt", "updated_dt", "clear_dt"];

function iterateObjectRecursively(obj: any, arr: string[]) {
  for (const key in obj) {
    pushObjectKeyAsContent(obj[key], key, arr);
  }
}

function pushObjectKeyAsContent(obj: any, key: string, arr: string[]) {
  if (obj) {
    const type_of_alarm_sub_key = typeof obj;
    if (type_of_alarm_sub_key === "string") {
      arr.push(`${titleCase(key)} : ${obj}`);
    } else if (type_of_alarm_sub_key === "object") {
      iterateObjectRecursively(obj, arr);
    }
  }
}

function getTimelineList(list: Alarm[]) {
  const final_arr: any[] = [];

  for (const alarm of list) {
    const _alarm = { ...alarm };
    if (_alarm.acked_user) {
      _alarm.acked_by_user = {
        "Acked By": `${_alarm.acked_user.first_name} ${_alarm.acked_user.last_name}`,
      };
    }
    for (const key of required_time_key_list) {
      const key_info = time_key_to_info_map[key as keyof typeof time_key_to_info_map];
      const content_arr: string[] = [];
      if (key_info?.sub_key_list) {
        for (const _sub_key of key_info.sub_key_list) {
          pushObjectKeyAsContent(_alarm[_sub_key], _sub_key, content_arr);
        }
      }
      if (key in _alarm && _alarm[key]) {
        const obj = {
          id: _alarm.id + key,
          time: _alarm[key],
          display_date: new Date(_alarm[key]).toLocaleString(),
          title: key_info.title,
          info: _alarm,
          content: content_arr,
          type: key_info.type,
        };

        final_arr.push(obj);
      }
    }
  }

  final_arr.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
  return final_arr;
}

const DEFAULT_LIMIT = 10;
const DEFAULT_OFFSET = 0;

const DEFAULT_MODAL_STATE = { open: false, info: {} };
function useAlarmTimeline() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<any[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const [fetchAlarmTimeline, { isFetching }] = useLazyGetAlarmTimelineQuery({});
  const [canFetchData, setCanFetchData] = useState<boolean>(true);
  const listOffset = useRef(DEFAULT_OFFSET);

  const [modalState, setModalState] = useState(DEFAULT_MODAL_STATE);

  function handleInfoModalOpen(info: any) {
    setModalState({ open: true, info });
  }

  function handleInfoModalClose() {
    setModalState(DEFAULT_MODAL_STATE);
  }

  async function fetchData() {
    const params = {
      device_ids: searchParams.get("device_id") ?? null,
      alarm_types: searchParams.get("alarm_type") ?? null,
      offset: listOffset.current,
      limit: DEFAULT_LIMIT,
    };

    const _data = await fetchAlarmTimeline(params).unwrap();
    const alarm_list = _data?.alarms;
    if (alarm_list.length === 0) {
      setCanFetchData(false);
      return;
    }
    setData((state) => [...state, ...alarm_list]);
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    function onScrollToBottom() {
      if (ref.current) {
        const { scrollTop, scrollHeight, clientHeight } = ref.current;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight;

        if (isNearBottom) {
          if (!canFetchData || isFetching) return;
          listOffset.current += DEFAULT_LIMIT;
          fetchData();
        }
      }
    }
    const listInnerElement = ref.current;

    if (listInnerElement) {
      listInnerElement.addEventListener("scroll", onScrollToBottom);

      return () => {
        listInnerElement.removeEventListener("scroll", onScrollToBottom);
      };
    }
  }, [canFetchData, isFetching]);

  const timeline_list = useMemo(() => getTimelineList(data), [data]);

  return { timeline_list, modalState, handleInfoModalOpen, handleInfoModalClose, ref, isFetching };
}

type AlarmTimelineType = "create" | "update" | "ack" | "clear";
type TimelineDotColor = "error" | "info" | "inherit" | "grey" | "primary" | "secondary" | "success" | "warning";
function getTimelineDotColor(title: AlarmTimelineType): TimelineDotColor {
  switch (title) {
    case "create":
      return "error";
    case "clear":
      return "success";
    default:
      return "warning";
  }
}

function getContentClass(type: AlarmTimelineType) {
  if (type === "ack") return "";
  return "timeline-content timeline-content_" + type;
}

const AlarmTimelineView = () => {
  const { timeline_list, modalState, handleInfoModalOpen, handleInfoModalClose, ref, isFetching } = useAlarmTimeline();

  return (
    <div className="timeline-view--page" ref={ref}>
      <div className="timeline-view--container">
        <Timeline
          sx={{
            [`& .${timelineOppositeContentClasses.root}`]: {
              flex: 0.2,
            },
          }}
        >
          {timeline_list.map((alarm: any, index: number) => (
            <TimelineItem key={alarm.id}>
              {alarm.display_date && (
                <TimelineOppositeContent color="text.secondary" sx={{ minWidth: "14rem" }}>
                  {alarm.display_date}
                </TimelineOppositeContent>
              )}
              <TimelineSeparator>
                <TimelineDot variant="filled" color={getTimelineDotColor(alarm.type)}></TimelineDot>
                {index !== timeline_list.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <div className={getContentClass(alarm.type)}>
                  <div className="flex items-center gap-1">
                    {alarm.title}
                    <InfoIcon className="cursor-pointer" onClick={() => handleInfoModalOpen(alarm.info)} />
                  </div>
                  {alarm.content.map((content: string, index: number) => (
                    <div key={index}>{content}</div>
                  ))}
                </div>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
        {isFetching && <div className="flex justify-center">{<CircularProgress />}</div>}
        {modalState.open && (
          <AlarmInfoPopup alarmInfo={modalState.info} open={modalState.open} onClose={handleInfoModalClose} />
        )}
      </div>
    </div>
  );
};

export default AlarmTimelineView;
